import isEqual from 'lodash.isequal';
import { RefObject } from 'react';
import { Color } from 'types/basic.types';
import { extendedChartColorPalette } from 'widgets/ChartWidgets/ChartWidget.config';
import { validateSpreadsheetColumns, validateSpreadsheetRows } from 'widgets/ChartWidgets/ChartWidget.helpers';
import { ChartDataSeries, ChartWidgetData } from 'widgets/ChartWidgets/ChartWidget.types';
import { getUpdatedDataClasses } from 'widgets/MapWidget/MapWidget.helpers';
import { MapWidgetData } from 'widgets/MapWidget/MapWidget.types';

import {
  ColorConfigProps,
  DataClassesConfigProps,
  ValidateResult,
  ValidateSeriesMetaProps,
} from './ChartSettings.types';

/**
 * extend a colors palette that the user is using on charts that have a data table on the side panel.
 * Depending on the data on the table, when more colors are required to match with the header,
 * extend the palette using the pre-defined color palette.
 *
 * @param seriesData ChartDataSeries, widgets/ChartWidgets/ChartWidget.types.ts
 * @param currentColors
 * @returns The extended color palette, if more color are needed, or the original color palette.
 */
export const updateColorPalette = (seriesData: ChartDataSeries, currentColors: Color[]): Color[] => {
  const { rowNum, seriesData: partialValidatedSeriesData } = validateSpreadsheetRows(seriesData);
  const validatedColumns = validateSpreadsheetColumns(partialValidatedSeriesData);

  const colors = [...currentColors];

  // For single series, row number map with the number of colors.
  // For multi series, column number map with the number of colors.
  // (note: as the type interface shown, the first column is the header, so the column need to minus 1)
  while (colors.length < rowNum || colors.length < validatedColumns.length - 1) {
    colors.push(...extendedChartColorPalette);
  }

  return colors;
};

/**
 * Validates the series meta data and determines if it needs updating based on the provided configurations.
 * @param {ValidateSeriesMetaProps} props - The series meta validation configurations.
 * @returns {ValidateResult} The result of the series meta validation.
 */
export const validateSeriesMeta = ({ colorConfig, dataClassesConfig }: ValidateSeriesMetaProps): ValidateResult => {
  const validateResult = {
    shouldUpdateSeriesMeta: false,
    updatedSeriesMeta: { colors: [] },
  };

  validateSeriesMetaColors(colorConfig, validateResult);
  validateSeriesMetaDataClasses(dataClassesConfig, validateResult);

  return validateResult;
};

/**
 * Gets the current colors from the provided `optionRef` or `seriesMeta`.
 * If the `optionRef` is provided and contains valid colors, it will be used.
 * Otherwise, it falls back to using the `seriesMeta` colors.
 * @param {RefObject<Pick<ChartWidgetData | MapWidgetData, 'seriesMeta'>> | undefined} optionRef - The reference to the `seriesMeta` data for the widget.
 * @param {ChartWidgetData['seriesMeta'] | MapWidgetData['seriesMeta'] | undefined} seriesMeta - The `seriesMeta` data for the widget.
 * @returns {string[]} An array of color values.
 */
const getCurrentColors = (
  optionRef: RefObject<Pick<ChartWidgetData | MapWidgetData, 'seriesMeta'>> | undefined,
  seriesMeta: ChartWidgetData['seriesMeta'] | MapWidgetData['seriesMeta'] | undefined,
): string[] => {
  if (optionRef && optionRef?.current && optionRef?.current?.seriesMeta && optionRef?.current?.seriesMeta?.colors) {
    return optionRef.current.seriesMeta.colors;
  } else if (seriesMeta) {
    return seriesMeta.colors;
  }
  return [];
};

/**
 * Validates and updates the colors of the series meta based on the given configuration.
 * @param {ColorConfigProps} config - The color configuration options.
 * @param {ValidateResult} validateResult - The result of the validation.
 */
const validateSeriesMetaColors = (
  { skipColorUpdate, updatedSeriesData, optionRef, seriesMeta }: ColorConfigProps,
  validateResult: ValidateResult,
) => {
  const currentColors = getCurrentColors(optionRef, seriesMeta);
  const colors = skipColorUpdate ? currentColors : updateColorPalette(updatedSeriesData, currentColors);

  const previousSeriesMeta = validateResult.updatedSeriesMeta;
  validateResult.updatedSeriesMeta = { ...previousSeriesMeta, colors };

  if (!isEqual(currentColors, colors)) {
    validateResult.shouldUpdateSeriesMeta = true;
  }
};

/**
 * Validates and updates the data classes of the series meta based on the given configuration.
 * @param {DataClassesConfigProps} config - The data classes configuration options.
 * @param {ValidateResult} validateResult - The result of the validation.
 */
const validateSeriesMetaDataClasses = (
  { shouldUpdateDataClasses, updatedSeriesData, optionRef, seriesMeta }: DataClassesConfigProps,
  validateResult: ValidateResult,
) => {
  const currentSeriesMeta = optionRef ? optionRef?.current?.seriesMeta : seriesMeta;
  const latestSeriesMeta = validateResult.updatedSeriesMeta;

  if (shouldUpdateDataClasses) {
    const {
      colorSteps,
      dataClasses,
      isCustomizeRangeEnabled = false,
    } = currentSeriesMeta as MapWidgetData['seriesMeta'];

    // If customize range is turned off, then we update the data classes
    if (!isCustomizeRangeEnabled) {
      validateResult.shouldUpdateSeriesMeta = true;
      validateResult.updatedSeriesMeta = {
        ...latestSeriesMeta,
        dataClasses: getUpdatedDataClasses(dataClasses, colorSteps, isCustomizeRangeEnabled, updatedSeriesData),
      };
    }
  }
};

export { getCurrentColors, validateSeriesMetaColors, validateSeriesMetaDataClasses };
